<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useMainStore } from '@/stores/main'
  import * as Server from '@/services/Server'
  import { useGameLink } from '@/services/GameLink'
  import * as Auth from '@/services/Auth'
  import DiscordButton from '@/components/DiscordButton.vue'
  import GamePlayButton from '@/components/GamePlayButton.vue'
  import PlayEventListLite from '@/components/PlayEventListLite.vue'
  import PlayNewsList from '@/components/PlayNewsList.vue'
  import NewsDialog from '@/components/NewsDialog.vue'
  import NewsFilterMenu from '@/components/NewsFilterMenu.vue'
  import { useNewsReader } from '@/composables/useNewsReader'
  import { enabledCategories, setEnabledCategories } from '@/lib/news'
  import { markSeenAutomatic } from '@/services/News'

  const store = useMainStore()

  /**
   * The count arrives with the launch link refresh (see services/GameLink): the call that keeps the
   * Play link valid also says how many players are in the game, every 30 seconds.
   */
  const { players: polledPlayers } = useGameLink()

  /**
   * The order the cards are shown in: the season first, then the stage races, then the session
   * events, then everything else (see PlayEventListLite). Anything not listed comes last; the
   * server order is kept inside a group (Array.sort is stable).
   */
  const EVENT_TYPE_ORDER = ['monthly_season', 'best_session_points', 'stages', 'best_lap_points']

  const eventTypeRank = (type) => {
    const index = EVENT_TYPE_ORDER.indexOf(type)
    return index === -1 ? EVENT_TYPE_ORDER.length : index
  }

  const status = ref(null)   // null = loading, true = online, false = offline
  const httpStatus = ref(null)
  const errorMessage = ref(null)
  const connectedPlayers = ref(null)
  const serverFull = ref(false) // no seat left: nobody else gets in until someone leaves

  // An account that cannot get in is not told how many are inside: the state of the room is about
  // the next click, which that account cannot make.
  const verified = computed(() => Auth.isVerified())

  // The live ping answers first when the page opens; then the refresh of the launch link takes
  // over and keeps the label current (a null answer leaves it where it is).
  watch(polledPlayers, value => {
    if (value !== null && value !== undefined) connectedPlayers.value = value
  })

  // Events a player can actually play right now — the play page is the player's home.
  const activeEvents = ref([])
  const eventsLoading = ref(true)

  // The player's messages: the footer of the play panel lights up with their count.
  const messages = ref([])

  // The last column: the player's news, and the unread ones its heading counts.
  const news = ref([])
  const unreadNews = ref(0)
  const newsLoading = ref(true)

  // The categories the reader wants to see (saved on the device, see lib/news). The filter icon
  // beside the heading is where this list is filtered: the same choice drives NewsView too.
  const newsFilter = ref(enabledCategories())

  // Opening a news marks it read (see useNewsReader); the badge follows without asking the server.
  const { selected: selectedNews, open: openNews, close: closeNews } = useNewsReader(() => {
    if (unreadNews.value > 0) unreadNews.value--
  })

  // A taste of the feed: the last eight news of the chosen categories, and the unread count the
  // server computed for the account.
  const loadNews = () => {
    newsLoading.value = true

    Server.get(store.apiPath.news(1, 8, newsFilter.value))
      .then(response => {
        news.value = response.data?.data?.news ?? []
        unreadNews.value = response.data?.data?.unread ?? 0
        // The automatic ones have no dialog: being shown is being read, from the next load on.
        markSeenAutomatic(news.value)
      })
      .catch(() => {
        news.value = []
        unreadNews.value = 0
      })
      .finally(() => {
        newsLoading.value = false
      })
  }

  const applyNewsFilter = (categories) => {
    newsFilter.value = categories
    setEnabledCategories(categories)
    loadNews()
  }

  /** Every unread news at once, instead of opening them one by one (see NewsLib::markAllRead). */
  const markAllNewsRead = () => {
    Server.put(store.apiPath.news_read_all, {})
      .then(() => loadNews())
      .catch(() => {})
  }

  const ping = () => {
    status.value = null
    errorMessage.value = null
    connectedPlayers.value = null
    serverFull.value = false

    Server.get(store.apiPath.game_ping)
      .then(response => {
        status.value = response.data.online
        httpStatus.value = response.data.status ?? null
        errorMessage.value = response.data.message ?? null
        connectedPlayers.value = response.data.connectedPlayers ?? 0
        serverFull.value = response.data.full === true
      })
      .catch(error => {
        status.value = false
        errorMessage.value = error.response?.data?.message ?? 'Connection error'
      })
  }

  /**
   * Running events — the play page is the player's home.
   *
   * `withStandings: false` asks the light list: the lite cards show the name, the closing date and
   * the activation switch, and the standings block is what makes this call expensive (it is
   * composed from the cached blob of every running event, see EventLib::list). The full list is
   * the one the old PlayEventList renders.
   */
  const loadActiveEvents = ({ withStandings = true } = {}) => {
    const path = withStandings ? store.apiPath.events_summary : store.apiPath.events_summary_lite

    Server.get(path)
      .then(response => {
        activeEvents.value = (response.data?.data ?? [])
          .filter(event => event.state === 'running')
          .sort((a, b) => eventTypeRank(a.eventType) - eventTypeRank(b.eventType))
      })
      .catch(() => {
        activeEvents.value = []
      })
      .finally(() => {
        eventsLoading.value = false
      })
  }

  /**
   * The activation choice of one card (see PlayEventListLite): only the flag changes, so the card
   * keeps every other field it was loaded with.
   */
  const applyActivation = ({ id, myActivation }) => {
    const event = activeEvents.value.find(item => item.id === id)
    if (event) event.myActivation = myActivation
  }

  onMounted(() => {
    ping()
    loadActiveEvents({ withStandings: false })
    loadNews()
  })
</script>

<template>
  <main>
    <v-container>
      <!-- Alpha test notice. The amber comes from the Vuetify class on the container, so the
           borders and the tinted background follow it through `currentColor`. -->
      <div class="alpha-banner text-amber d-flex align-center flex-wrap ga-3 mt-8 px-4 py-3">
        <v-icon size="30">mdi-flask-outline</v-icon>

        <div>
          <p class="alpha-banner__label d-flex ga-2 mb-1">
            <span>{{ $t('alphaTest.alpha') }}</span>
            <span class="text-white">{{ $t('alphaTest.test') }}</span>
          </p>
          <p class="text-body-2 text-medium-emphasis mb-0">{{ $t('alphaTest.message') }}</p>
        </div>
      </div>

      <!-- The way in, as a panel: the headline, the state of the room and the button down the middle.
           The shortcuts get a footer of their own: they are things you set up around a race, and the
           green stays the only call to action of the page. -->
      <v-card elevation="3" rounded="lg" class="hero-card mt-6">
        <div class="hero">
          <div class="hero__lead">
            <!-- <h1 class="hero__title text-h4">{{ $t('play.title') }}</h1> -->

            <div v-if="status === null" class="d-flex align-center ga-2">
              <v-progress-circular indeterminate size="18" color="primary" />
              <span class="text-medium-emphasis">{{ $t('play.connecting') }}</span>
            </div>

            <template v-else-if="status">
              <!-- Nobody else around: the room is a hotlap session, so say that instead of "0 players". -->
              <p v-if="verified" class="hero__players mb-0">
                <span class="hero__dot" :class="{ 'hero__dot--live': connectedPlayers > 0 }"></span>
                {{ connectedPlayers > 0
                  ? $t('play.playersOnline', { count: connectedPlayers }, connectedPlayers)
                  : $t('play.hotlapping') }}
              </p>
              <p v-if="verified && serverFull" class="text-warning text-body-2 mb-0">
                {{ $t('play.serverFullHint') }}
              </p>
              <p v-if="httpStatus && httpStatus != 200" class="text-medium-emphasis text-body-2 mb-0">
                HTTP {{ httpStatus }}
              </p>
            </template>

            <template v-else>
              <v-chip color="error" size="small" variant="tonal" label prepend-icon="mdi-alert-circle">
                {{ $t('play.offline') }}
              </v-chip>
              <p v-if="errorMessage" class="text-medium-emphasis mb-0">
                {{ errorMessage }}
              </p>
            </template>
          </div>

          <div class="hero__actions">
            <GamePlayButton
              v-if="status"
              class="btn-mega"
              :label="serverFull ? $t('play.serverFull') : $t('play.playNow')"
              :color="serverFull ? 'warning' : 'success'"
              :prepend-icon="serverFull ? 'mdi-account-cancel' : 'mdi-play-circle'"
              :disabled="serverFull"
              size="large"
            />
            <v-btn
              v-else
              class="btn-mega"
              :color="status === false ? 'error' : undefined"
              :loading="status === null"
              :disabled="status === null"
              :prepend-icon="status === false ? 'mdi-refresh' : undefined"
              size="large"
              @click="ping"
            >
              {{ status === false ? $t('play.retry') : $t('play.playNow') }}
            </v-btn>

            <!-- The room frees up on its own: re-checking is one click instead of a page reload. -->
            <v-btn
              v-if="verified && serverFull"
              variant="text"
              size="small"
              prepend-icon="mdi-refresh"
              @click="ping"
            >
              {{ $t('play.retry') }}
            </v-btn>
          </div>
        </div>

        <!-- The shortcuts around a race: the footer of the panel, one step below the way in -->
        <div class="hero__links">
          <!-- The messages: their entry point, lighting up only when there is something to read
               (their page is still to come) -->
          <v-btn
            v-if="messages.length"
            color="primary"
            size="small"
            variant="text"
            prepend-icon="mdi-email-outline"
          >
            {{ $t('play.messages') }}
            <v-chip class="ml-2" color="green" size="x-small" variant="flat" label>{{ messages.length }}</v-chip>
          </v-btn>

          <v-btn
            color="primary"
            size="small"
            variant="text"
            prepend-icon="mdi-tune"
            @click="$router.push({ name: 'settings', query: { tab: 'game' } })"
          >
            {{ $t('play.gameOptions') }}
          </v-btn>
          <v-btn
            color="warning"
            size="small"
            variant="text"
            prepend-icon="mdi-bell"
            @click="$router.push({ name: 'settings', query: { tab: 'notifications' } })"
          >
            {{ $t('play.notifyMe') }}
          </v-btn>
          <DiscordButton variant="text" size="small" />
        </div>
      </v-card>

      <!-- Running events: the play page holds everything a player needs -->
      <v-row class="mt-6">
        <v-col cols="12" lg="6">
          <section class="panel">
            <div class="panel__head">
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('events.openTitle') }}</h2>
              <v-chip v-if="activeEvents.length" color="green" size="small" variant="tonal" label>
                {{ activeEvents.length }}
              </v-chip>
            </div>

            <div class="panel__body">
              <div v-if="eventsLoading" class="d-flex justify-center py-8">
                <v-progress-circular indeterminate color="primary" />
              </div>

              <PlayEventListLite v-else :events="activeEvents" @change="applyActivation" />

              <v-alert v-if="!eventsLoading && !activeEvents.length" type="info" variant="tonal">
                {{ $t('events.noActive') }}
              </v-alert>
            </div>
          </section>
        </v-col>

        <!-- The player's news: the heading counts the unread ones, the list opens the dialog -->
        <v-col cols="12" lg="6">
          <section class="panel">
            <div class="panel__head">
              <h2 class="text-h6 font-weight-bold mb-0">{{ $t('news.title') }}</h2>
              <v-chip v-if="unreadNews" color="green" size="small" variant="tonal" label>
                {{ unreadNews }}
              </v-chip>
              <v-spacer />
              <v-btn
                v-if="unreadNews > 0"
                icon="mdi-email-open-outline"
                size="small"
                density="comfortable"
                variant="tonal"
                :title="$t('news.markAllRead')"
                @click="markAllNewsRead"
              />
              <NewsFilterMenu :model-value="newsFilter" @update:model-value="applyNewsFilter" />
            </div>

            <div class="panel__body">
              <PlayNewsList :news="news" :loading="newsLoading" @open="openNews" />

              <v-alert
                v-if="!newsLoading && !news.length"
                color="info"
                variant="tonal"
                icon="mdi-newspaper-variant-outline"
              >
                {{ $t('news.empty') }}
              </v-alert>

              <div class="mt-2 text-right">
                <router-link :to="{ name: 'news' }" class="text-caption text-medium-emphasis">
                  {{ $t('news.all') }}
                </router-link>
              </div>
            </div>
          </section>
        </v-col>
      </v-row>
    </v-container>

    <NewsDialog :news="selectedNews" @close="closeNews" />
  </main>
</template>

<style scoped>
.alpha-banner {
  border: 1px solid color-mix(in srgb, currentColor 45%, transparent);
  border-left: 4px solid currentColor;
  border-radius: 8px;
  background: color-mix(in srgb, currentColor 12%, transparent);
}

.alpha-banner__label {
  font-size: .95rem;
  letter-spacing: .2em;
  line-height: 1.2;
  text-transform: uppercase;
}

/* The way in, as a panel: the headline, the state of the room and the button down the middle, the
   shortcuts on a footer strip of their own. The glow gives the panel depth without a picture. */
.hero-card {
  overflow: hidden;
  background-image: radial-gradient(120% 150% at 50% -30%, rgba(var(--v-theme-primary), .20), rgba(var(--v-theme-primary), 0) 60%);
}

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 24px;
  text-align: center;
}

.hero__lead {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.hero__title {
  margin: 0;
  font-weight: 700;
  letter-spacing: -.01em;
}

.hero__players {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, .78);
}

/* The state of the room at a glance: the light is on when there is somebody in it. */
.hero__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .28);
}

.hero__dot--live {
  background: rgb(var(--v-theme-success));
  box-shadow: 0 0 0 4px rgba(var(--v-theme-success), .18);
}

.hero__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  margin-top: 6px;
}

.hero__actions > .btn-mega {
  min-height: 64px;
}

/* The shortcuts: a footer of the panel, one step below the way in. */
.hero__links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 18px;
  border-top: 1px solid rgba(255, 255, 255, .08);
  background: rgba(0, 0, 0, .16);
}

/* Two sections, two troughs: a column that is plainly a box of its own needs no gutter trick to be
   told apart, and the heading that opens it sits on the box it belongs to. Darker than the page —
   the event cards and the news rows inside are lighter — so the contents keep standing out, and the
   radius is wider than theirs so the two roundings read as concentric. */
.panel {
  border: 1px solid rgba(var(--v-theme-on-surface), .09);
  border-radius: 12px;
  background: rgba(0, 0, 0, .22);
}

/* The heading and the icons beside it are the toolbar of the box, and the rule under them says so.
   The head is as tall as its title (a .text-h6 line box) and no more: the icon buttons in it are
   comfortable for that reason — at their default density they are 40px and would push this head
   8px taller than the one of the panel next to it, with no icon at all. */
.panel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), .09);
}

.panel__body {
  padding: 16px;
}

/* Slightly lighter than the card surface it sits on, so each rule reads as its own row. */
.instruction-row {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 8px;
  padding: 12px 16px;
}
</style>
